import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { animate, state, style, transition, trigger } from '@angular/animations'

import { Subject } from 'rxjs'
import { take, takeUntil } from 'rxjs/operators'

import { User } from '@overtur-monorepo/keying-user'
import { KeyingAuthService } from '../../../../services/keying.auth.service'
import { OrderLookupService } from '@overtur-monorepo/keying-hierarchy'
import { OrderDetails } from '@overtur-monorepo/keying-shared'
import { MatButtonToggleChange } from '@angular/material/button-toggle'
import { MatTabChangeEvent } from '@angular/material/tabs'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'

@Component({
    selector: 'app-find-orders',
    templateUrl: './find-orders.component.html',
    styleUrls: ['./find-orders.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FindOrdersComponent implements OnInit, OnDestroy, AfterViewInit {
  destroy$ = new Subject()

  columnsToDisplay = [
    'orderId',
    'orderNumber',
    'structureNumber',
    'isRestricted',
    'userName',
    'createdDate',
    'inbound',
    'outbound',
    'requestId',
  ]
  columnsToDisplayWithExpand = [...this.columnsToDisplay, 'expand']
  expandedElement: OrderDetails | null
  dataSource: MatTableDataSource<OrderDetails> = new MatTableDataSource()

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator

  showPaginator = false
  pageSize = 10
  pageSizeOptions = [5, 10, 20, 50]

  user: User
  userId?: number

  orderNumber: string

  structureNumber: string

  timeRange = 'none'
  timeRangeInDays?: number

  timeRangeOptions = [
    { label: 'Last 24 hrs', value: '24h' },
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'Last 90 days', value: '90d' },
    { label: 'Last 180 days', value: '180d' },
  ]

  selectedUserOption = 'All Users'

  oeStatus: string
  rpStatus: string

  showNoData = false

  focus = {
    searchyBy: false,
    orderNumber: false,
  }

  /* istanbul ignore next */
  constructor(
    private keyingAuthService: KeyingAuthService,
    private orderLookupService: OrderLookupService,
    private cdr: ChangeDetectorRef
  ) {
    this.keyingAuthService.localAuthSetup()
    this.keyingAuthService.handleAuthCallback()
  }

  /* istanbul ignore next */
  ngOnInit(): void {
    this.keyingAuthService.keyingUser$.pipe(takeUntil(this.destroy$)).subscribe((usr) => {
      if (!usr) {
        return
      }
      this.user = usr
      this.userId = usr.id
      this.cdr.markForCheck()
    })
  }

  /* istanbul ignore next */
  ngOnDestroy() {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator
  }

  /* istanbul ignore next */
  pageChanged(event: PageEvent) {
    this.dataSource.paginator.pageIndex = event.pageIndex
    this.dataSource.paginator.pageSize = event.pageSize
  }

  /* istanbul ignore next */
  onTimeRangeChange(value: string): void {
    this.showNoData = false

    this.timeRange = value

    if (this.timeRange === '24h') {
      this.timeRangeInDays = 1
    } else if (this.timeRange === '7d') {
      this.timeRangeInDays = 7
    } else if (this.timeRange === '30d') {
      this.timeRangeInDays = 30
    } else if (this.timeRange === '90d') {
      this.timeRangeInDays = 90
    } else if (this.timeRange === '180d') {
      this.timeRangeInDays = 180
    } else {
      this.timeRangeInDays = null
    }
  }

  /* istanbul ignore next */
  onUserToggleChange(event: MatButtonToggleChange): void {
    this.showNoData = false

    this.selectedUserOption = event.value

    if (this.selectedUserOption === 'Current User') {
      this.userId = this.user.id
    }
  }

  /* istanbul ignore next */
  getOrderDetails() {
    let id = this.userId
    if (this.selectedUserOption === 'All Users') {
      id = null
    }

    this.orderLookupService
      .getOrderDetails(this.orderNumber, this.structureNumber, id, this.timeRangeInDays)
      .pipe(take(1))
      .subscribe((orders) => {
        this.dataSource.data = orders
        this.showNoData = this.dataSource.data.length === 0
        this.showPaginator = this.dataSource.data.length > 0
        this.cdr.markForCheck()
      })
  }

  /* istanbul ignore next */
  disableButtons() {
    return !this.orderNumber && !this.structureNumber
  }

  /* istanbul ignore next */
  clearFields() {
    this.orderNumber = ''
    this.structureNumber = ''
    this.timeRange = 'none'
    this.userId = this.user.id
    this.selectedUserOption = 'All Users'
    this.dataSource.data = []
  }

  /* istanbul ignore next */
  copyFileName(fileName: string): void {
    navigator.clipboard.writeText(fileName).then(
      () => {
        // console.log('Copied to clipboard:', fileName)
      },
      (err) => {
        // console.error('Error copying to clipboard:', err)
      }
    )
  }

  /* istanbul ignore next */
  onTabChange(event: MatTabChangeEvent, row: OrderDetails) {
    // console.log('row', row)
    if (event.index === 1) {
      this.getOrderEntryStatus(row.requestId)
    } else if (event.index === 2) {
      this.getRestrictedProductStatus(row.orderNumber, row.requestId)
    }
  }

  /* istanbul ignore next */
  getOrderEntryStatus(requestId: string) {
    this.orderLookupService
      .getOrderEntryStatus(requestId)
      .pipe(take(1))
      .subscribe((status) => {
        this.oeStatus = status
        this.cdr.markForCheck()
      })
  }

  /* istanbul ignore next */
  getRestrictedProductStatus(orderNumber: string, requestId: string) {
    this.orderLookupService
      .getRestrictedProductStatus(orderNumber, requestId)
      .pipe(take(1))
      .subscribe((status) => {
        // console.log('rp response', status)
        this.rpStatus = status
        this.cdr.markForCheck()
      })
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)
    return Boolean(field.invalid && (field.dirty || field.touched))
  }
}
